import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import {
    getAdminMyRebabyPhase,
    getAdminMyRebabyPhases,
    updateAdminMyRebabyPhase,
    getAllLanguagesAdmin,
} from '../../services/api.admin';
import type {
    AdminMyRebabyHighlightDto,
    AdminMyRebabyPhaseDetailDto,
    AdminMyRebabyPhaseListDto,
    AdminUpdateMyRebabyPhaseContentDto,
    LanguageAdminDto,
} from '../../types';

type EmojiTarget =
    | { kind: 'phase' }
    | { kind: 'whats' }
    | { kind: 'tip' }
    | { kind: 'highlight'; index: number };

const emptyLangMap = (langs: string[]): Record<string, string> =>
    Object.fromEntries(langs.map((l) => [l, '']));

const MyRebabyDesignerPanel = () => {
    const [phases, setPhases] = useState<AdminMyRebabyPhaseListDto[] | null>(null);
    const [languages, setLanguages] = useState<LanguageAdminDto[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detail, setDetail] = useState<AdminMyRebabyPhaseDetailDto | null>(null);
    const [editLang, setEditLang] = useState('en');
    const [previewLang, setPreviewLang] = useState('en');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [emojiTarget, setEmojiTarget] = useState<EmojiTarget | null>(null);

    // Draft form state
    const [icon, setIcon] = useState('');
    const [displayName, setDisplayName] = useState<Record<string, string>>({});
    const [showWhats, setShowWhats] = useState(true);
    const [whatsEmoji, setWhatsEmoji] = useState('💬');
    const [whatsTitle, setWhatsTitle] = useState<Record<string, string>>({});
    const [whatsBody, setWhatsBody] = useState<Record<string, string>>({});
    const [showHighlights, setShowHighlights] = useState(true);
    const [highlights, setHighlights] = useState<AdminMyRebabyHighlightDto[]>([]);
    const [showTip, setShowTip] = useState(true);
    const [tipEmoji, setTipEmoji] = useState('💡');
    const [tipText, setTipText] = useState<Record<string, string>>({});
    const [accentColor, setAccentColor] = useState('#CFFAFE');

    const langCodes = useMemo(
        () => (languages.length > 0 ? languages.map((l) => l.code) : ['en', 'nl-be', 'fr-be']),
        [languages]
    );

    const loadList = useCallback(async () => {
        const [list, langs] = await Promise.all([
            getAdminMyRebabyPhases(),
            getAllLanguagesAdmin().catch(() => [] as LanguageAdminDto[]),
        ]);
        setPhases(list);
        setLanguages(Array.isArray(langs) ? langs : []);
        if (langs?.length) {
            const first = langs[0].code || 'en';
            setEditLang((prev) => (langs.some((l) => l.code === prev) ? prev : first));
            setPreviewLang((prev) => (langs.some((l) => l.code === prev) ? prev : first));
        }
    }, []);

    useEffect(() => {
        loadList().catch((e) => setError(String(e)));
    }, [loadList]);

    const applyDetail = (d: AdminMyRebabyPhaseDetailDto, langs: string[]) => {
        setDetail(d);
        setIcon(d.icon ?? '');
        setDisplayName({ ...emptyLangMap(langs), ...d.displayName });
        setShowWhats(d.showWhatsHappening);
        setWhatsEmoji(d.whatsHappeningEmoji ?? '💬');
        setWhatsTitle({ ...emptyLangMap(langs), ...d.whatsHappeningTitle });
        setWhatsBody({ ...emptyLangMap(langs), ...d.whatsHappeningBody });
        setShowHighlights(d.showDevelopmentHighlights);
        setHighlights(
            (d.highlights ?? []).map((h) => ({
                ...h,
                text: { ...emptyLangMap(langs), ...h.text },
            }))
        );
        setShowTip(d.showTip);
        setTipEmoji(d.tipEmoji ?? '💡');
        setTipText({ ...emptyLangMap(langs), ...d.tipText });
        setAccentColor(d.accentColor ?? '#CFFAFE');
    };

    const openPhase = async (phaseId: number) => {
        setSelectedId(phaseId);
        setMessage(null);
        setError(null);
        setLoading(true);
        try {
            const d = await getAdminMyRebabyPhase(phaseId);
            applyDetail(d, langCodes);
        } catch {
            setError('Failed to load phase content');
        } finally {
            setLoading(false);
        }
    };

    const setLangField = (
        setter: Dispatch<SetStateAction<Record<string, string>>>,
        value: string
    ) => {
        setter((prev) => ({ ...prev, [editLang]: value }));
    };

    const addHighlight = () => {
        setHighlights((prev) => [
            ...prev,
            {
                sortOrder: prev.length,
                isVisible: true,
                emoji: '✨',
                text: emptyLangMap(langCodes),
            },
        ]);
    };

    const removeHighlight = (index: number) => {
        setHighlights((prev) => prev.filter((_, i) => i !== index).map((h, i) => ({ ...h, sortOrder: i })));
    };

    const handleEmojiClick = (data: EmojiClickData) => {
        if (!emojiTarget) return;
        const e = data.emoji;
        if (emojiTarget.kind === 'phase') setIcon(e);
        else if (emojiTarget.kind === 'whats') setWhatsEmoji(e);
        else if (emojiTarget.kind === 'tip') setTipEmoji(e);
        else if (emojiTarget.kind === 'highlight') {
            setHighlights((prev) =>
                prev.map((h, i) => (i === emojiTarget.index ? { ...h, emoji: e } : h))
            );
        }
        setEmojiTarget(null);
    };

    const handleSave = async () => {
        if (selectedId == null) return;
        setLoading(true);
        setError(null);
        setMessage(null);
        const dto: AdminUpdateMyRebabyPhaseContentDto = {
            icon: icon.trim() || null,
            displayName,
            showWhatsHappening: showWhats,
            whatsHappeningEmoji: whatsEmoji.trim() || null,
            whatsHappeningTitle: whatsTitle,
            whatsHappeningBody: whatsBody,
            showDevelopmentHighlights: showHighlights,
            highlights: highlights.map((h, i) => ({
                sortOrder: i,
                isVisible: h.isVisible,
                emoji: h.emoji,
                text: h.text,
            })),
            showTip,
            tipEmoji: tipEmoji.trim() || null,
            tipText,
            accentColor: accentColor.trim() || null,
        };
        try {
            const updated = await updateAdminMyRebabyPhase(selectedId, dto);
            applyDetail(updated, langCodes);
            setMessage('Saved. Translations cache cleared on server.');
            await loadList();
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'response' in e
                    ? (e as { response?: { data?: { errors?: { General?: string } } } }).response?.data
                          ?.errors?.General
                    : null;
            setError(msg || 'Save failed');
        } finally {
            setLoading(false);
        }
    };

    const tPreview = (map: Record<string, string>, fallback = '') =>
        map[previewLang] || map['en'] || map[Object.keys(map)[0]] || fallback;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-800">My Journey designer</h1>
                <p className="mt-1 text-sm text-gray-600">
                    Customize What&apos;s Happening, Development Highlights, and Tips per growth phase.
                    Copy is stored in Translations (all languages). New phases appear here automatically.
                </p>
            </div>

            {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</div>
            )}
            {message && (
                <div className="rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
                    {message}
                </div>
            )}

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                {/* Phase list */}
                <div className="xl:col-span-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Phases</h2>
                    <ul className="max-h-[70vh] space-y-1 overflow-y-auto">
                        {(phases ?? []).map((p) => (
                            <li key={p.phaseId}>
                                <button
                                    type="button"
                                    onClick={() => openPhase(p.phaseId)}
                                    className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition ${
                                        selectedId === p.phaseId
                                            ? 'bg-green-100 font-medium text-gray-900'
                                            : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                >
                                    <span className="text-lg">{p.icon ?? '👶'}</span>
                                    <span className="min-w-0 flex-1 truncate">
                                        {p.minAgeMonths}–{p.maxAgeMonths}m · {p.name}
                                    </span>
                                    {!p.hasContent && (
                                        <span className="text-[10px] text-amber-600">new</span>
                                    )}
                                </button>
                            </li>
                        ))}
                        {phases?.length === 0 && (
                            <li className="text-sm text-gray-500">No phases in database.</li>
                        )}
                    </ul>
                </div>

                {/* Editor */}
                <div className="xl:col-span-5 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                    {!detail ? (
                        <p className="text-sm text-gray-500">Select a phase to edit educational content.</p>
                    ) : (
                        <div className="space-y-5">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h2 className="text-xl font-bold text-gray-800">
                                    {detail.minAgeMonths}–{detail.maxAgeMonths}m
                                </h2>
                                <button
                                    type="button"
                                    disabled={loading}
                                    onClick={handleSave}
                                    className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                                >
                                    {loading ? 'Saving…' : 'Save'}
                                </button>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-semibold text-gray-600">
                                    Edit language
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {langCodes.map((code) => (
                                        <button
                                            key={code}
                                            type="button"
                                            onClick={() => setEditLang(code)}
                                            className={`rounded-full px-3 py-1 text-xs font-medium ${
                                                editLang === code
                                                    ? 'bg-gray-800 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                        >
                                            {code}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-semibold text-gray-600">Phase icon</label>
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl">{icon || '—'}</span>
                                    <button
                                        type="button"
                                        className="rounded border px-2 py-1 text-xs"
                                        onClick={() => setEmojiTarget({ kind: 'phase' })}
                                    >
                                        Pick emoji
                                    </button>
                                    <button type="button" className="text-xs text-gray-500 underline" onClick={() => setIcon('')}>
                                        Clear
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-semibold text-gray-600">
                                    Display name (under age range, e.g. Newborn)
                                </label>
                                <input
                                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                    placeholder="myrebaby.phase.{id}.displayName"
                                    value={displayName[editLang] ?? ''}
                                    onChange={(e) => setLangField(setDisplayName, e.target.value)}
                                />
                                <p className="mt-1 text-[11px] text-gray-500">
                                    Shown under &quot;0–2 months&quot; in the journey header (Figma style). Key:{' '}
                                    <code className="text-[10px]">myrebaby.phase.{detail.phaseId}.displayName</code>
                                </p>
                            </div>

                            <div>
                                <label className="mb-1 block text-xs font-semibold text-gray-600">
                                    Phase accent (strong) — Figma color
                                </label>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="color"
                                        value={/^#[0-9A-Fa-f]{6}$/.test(accentColor) ? accentColor : '#22D3EE'}
                                        onChange={(e) => setAccentColor(e.target.value)}
                                        className="h-10 w-14 cursor-pointer rounded border"
                                    />
                                    <input
                                        type="text"
                                        value={accentColor}
                                        onChange={(e) => setAccentColor(e.target.value)}
                                        placeholder="#22D3EE"
                                        className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm font-mono"
                                    />
                                    <span
                                        className="h-10 w-10 rounded border"
                                        style={{ backgroundColor: accentColor }}
                                        title="Strong"
                                    />
                                    <span
                                        className="h-10 w-10 rounded border"
                                        style={{
                                            backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(accentColor)
                                                ? `color-mix(in srgb, ${accentColor} 16%, white)`
                                                : '#f5f5f5',
                                        }}
                                        title="Soft (derived in shop)"
                                    />
                                </div>
                                <p className="mt-1 text-[11px] text-gray-500">
                                    Strong color for chip bottom bar, checkboxes, progress. Soft backgrounds are derived
                                    in the shop (~16% mix with white) — pick a saturated accent like Figma, not a pastel.
                                </p>
                            </div>

                            {/* What's happening */}
                            <section className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3">
                                <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                                    <input
                                        type="checkbox"
                                        checked={showWhats}
                                        onChange={(e) => setShowWhats(e.target.checked)}
                                    />
                                    Show What&apos;s Happening
                                </label>
                                <div className="flex items-center gap-2">
                                    <span className="text-xl">{whatsEmoji}</span>
                                    <button
                                        type="button"
                                        className="rounded border px-2 py-1 text-xs"
                                        onClick={() => setEmojiTarget({ kind: 'whats' })}
                                    >
                                        Emoji
                                    </button>
                                </div>
                                <input
                                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                    placeholder="Section title"
                                    value={whatsTitle[editLang] ?? ''}
                                    onChange={(e) => setLangField(setWhatsTitle, e.target.value)}
                                />
                                <textarea
                                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                    rows={3}
                                    placeholder="Body text"
                                    value={whatsBody[editLang] ?? ''}
                                    onChange={(e) => setLangField(setWhatsBody, e.target.value)}
                                />
                            </section>

                            {/* Highlights */}
                            <section className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                                        <input
                                            type="checkbox"
                                            checked={showHighlights}
                                            onChange={(e) => setShowHighlights(e.target.checked)}
                                        />
                                        Show Development Highlights
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addHighlight}
                                        className="text-xs font-medium text-green-700 underline"
                                    >
                                        + Add line
                                    </button>
                                </div>
                                {highlights.map((h, index) => (
                                    <div key={index} className="flex flex-col gap-2 rounded border bg-white p-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-xl">{h.emoji ?? '✨'}</span>
                                            <button
                                                type="button"
                                                className="rounded border px-2 py-0.5 text-xs"
                                                onClick={() => setEmojiTarget({ kind: 'highlight', index })}
                                            >
                                                Emoji
                                            </button>
                                            <label className="flex items-center gap-1 text-xs text-gray-600">
                                                <input
                                                    type="checkbox"
                                                    checked={h.isVisible}
                                                    onChange={(e) =>
                                                        setHighlights((prev) =>
                                                            prev.map((x, i) =>
                                                                i === index ? { ...x, isVisible: e.target.checked } : x
                                                            )
                                                        )
                                                    }
                                                />
                                                Visible
                                            </label>
                                            <button
                                                type="button"
                                                className="ml-auto text-xs text-red-600 underline"
                                                onClick={() => removeHighlight(index)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        <input
                                            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                            placeholder="Highlight text"
                                            value={h.text[editLang] ?? ''}
                                            onChange={(e) =>
                                                setHighlights((prev) =>
                                                    prev.map((x, i) =>
                                                        i === index
                                                            ? {
                                                                  ...x,
                                                                  text: { ...x.text, [editLang]: e.target.value },
                                                              }
                                                            : x
                                                    )
                                                )
                                            }
                                        />
                                    </div>
                                ))}
                            </section>

                            {/* Tip */}
                            <section className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3">
                                <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                                    <input type="checkbox" checked={showTip} onChange={(e) => setShowTip(e.target.checked)} />
                                    Show tip box
                                </label>
                                <div className="flex items-center gap-2">
                                    <span className="text-xl">{tipEmoji}</span>
                                    <button
                                        type="button"
                                        className="rounded border px-2 py-1 text-xs"
                                        onClick={() => setEmojiTarget({ kind: 'tip' })}
                                    >
                                        Emoji
                                    </button>
                                </div>
                                <textarea
                                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                    rows={2}
                                    placeholder="Tip text"
                                    value={tipText[editLang] ?? ''}
                                    onChange={(e) => setLangField(setTipText, e.target.value)}
                                />
                            </section>

                            {emojiTarget && (
                                <div className="rounded-lg border border-gray-200 p-2">
                                    <div className="mb-2 flex justify-between text-xs text-gray-500">
                                        <span>Pick emoji</span>
                                        <button type="button" onClick={() => setEmojiTarget(null)}>
                                            Close
                                        </button>
                                    </div>
                                    <EmojiPicker onEmojiClick={handleEmojiClick} width="100%" height={320} />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Live preview */}
                <div className="xl:col-span-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Live preview</h2>
                        <div className="flex flex-wrap gap-1">
                            {langCodes.map((code) => (
                                <button
                                    key={code}
                                    type="button"
                                    onClick={() => setPreviewLang(code)}
                                    className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                        previewLang === code
                                            ? 'bg-green-700 text-white'
                                            : 'bg-gray-100 text-gray-600'
                                    }`}
                                >
                                    {code}
                                </button>
                            ))}
                        </div>
                    </div>
                    {!detail ? (
                        <p className="text-sm text-gray-400">Preview appears when a phase is selected.</p>
                    ) : (
                        <div className="space-y-3 rounded-2xl border border-gray-100 bg-[#FAF5DC]/40 p-4">
                            <div className="mb-2 flex flex-col items-center text-sm font-bold text-gray-800">
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl">{icon || '👶'}</span>
                                    {detail.minAgeMonths}–{detail.maxAgeMonths}m
                                </div>
                                {(displayName[previewLang] || displayName['en']) && (
                                    <span className="mt-0.5 text-xs font-normal text-gray-500">
                                        {displayName[previewLang] || displayName['en']}
                                    </span>
                                )}
                            </div>
                            {showWhats && (
                                <div className="rounded-2xl border border-green-200/80 bg-green-50/80 p-4">
                                    <div className="mb-1.5 flex items-center gap-2">
                                        <span className="text-xl">{whatsEmoji}</span>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-800">
                                            {tPreview(whatsTitle, "WHAT'S HAPPENING")}
                                        </p>
                                    </div>
                                    <p className="text-sm leading-relaxed text-gray-800">
                                        {tPreview(whatsBody, '—')}
                                    </p>
                                </div>
                            )}
                            {showHighlights && highlights.some((h) => h.isVisible) && (
                                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Development highlights
                                    </p>
                                    <ul className="space-y-2 text-sm text-gray-800">
                                        {highlights
                                            .filter((h) => h.isVisible)
                                            .map((h, i) => (
                                                <li key={i} className="flex items-start gap-2">
                                                    <span>{h.emoji ?? '•'}</span>
                                                    <span>{tPreview(h.text, '—')}</span>
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            )}
                            {showTip && (
                                <div className="rounded-2xl border border-gray-200 bg-white p-3.5 text-sm text-gray-800">
                                    <span className="mr-1">{tipEmoji}</span>
                                    {tPreview(tipText, '—')}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyRebabyDesignerPanel;
